import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: pages in src/pages/insurance/ are one level deeper than src/pages/, requiring three levels up to reach convex/_generated/
import { useOrgContext } from '@/hooks/useOrgContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { FileText, Plus, Edit, Trash2, Check, Layers } from 'lucide-react';
import type { IClaimFormTemplate, TClaimFormCategory } from '@/@types/claims';
import { cn } from '@/lib/utils';

const CATEGORY_COLORS: Record<TClaimFormCategory, string> = {
  health: 'bg-green-100 text-green-800 border-green-200',
  motor: 'bg-blue-100 text-blue-800 border-blue-200',
  home: 'bg-amber-100 text-amber-800 border-amber-200',
  custom: 'bg-purple-100 text-purple-800 border-purple-200',
};

function CategoryBadge({ category }: { category: TClaimFormCategory }) {
  return (
    <Badge
      variant="outline"
      className={cn('capitalize', CATEGORY_COLORS[category])}
    >
      {category}
    </Badge>
  );
}

function TemplateCardSkeleton() {
  return (
    <Card>
      <CardHeader className="pb-3">
        <Skeleton className="h-5 w-40 mb-1" />
        <Skeleton className="h-4 w-24" />
      </CardHeader>
      <CardContent>
        <Skeleton className="h-4 w-32 mb-4" />
        <div className="flex gap-2">
          <Skeleton className="h-9 w-16" />
          <Skeleton className="h-9 w-16" />
        </div>
      </CardContent>
    </Card>
  );
}

function TemplateCard({
  template,
  onDelete,
}: {
  template: IClaimFormTemplate;
  onDelete: (id: string) => void;
}) {
  const navigate = useNavigate();

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">{template.name}</CardTitle>
          {template.isDefault && (
            <span className="flex items-center gap-1 text-xs text-green-700 font-medium shrink-0">
              <Check className="w-3.5 h-3.5" />
              Default
            </span>
          )}
        </div>
        <CardDescription className="flex items-center gap-2 mt-1">
          {/* AS_JUSTIFIED: templateCategory from Convex return narrowed to TClaimFormCategory literal union */}
          <CategoryBadge category={template.templateCategory as TClaimFormCategory} />
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3 flex-1">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
          <Layers className="w-4 h-4" />
          {template.sections.length} section{template.sections.length !== 1 ? 's' : ''}
        </div>
        <div className="flex gap-2 mt-auto pt-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate(`/insurance/claim-forms/${template._id}`)}
          >
            <Edit className="w-3.5 h-3.5 mr-1.5" />
            Edit
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="text-destructive hover:text-destructive">
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Delete
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete template?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will permanently delete &quot;{template.name}&quot;. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive hover:bg-destructive/90"
                  onClick={() => onDelete(template._id)}
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </CardContent>
    </Card>
  );
}

export function ClaimFormsListPage() {
  const navigate = useNavigate();
  const { currentOrg, isLoading: orgLoading } = useOrgContext();
  const [isIniting, setIsIniting] = useState(false);

  const templates = useQuery(
    api.insurance.claimFormTemplates.list,
    currentOrg ? { orgId: currentOrg._id } : 'skip'
  );
  const initTemplates = useMutation(api.insurance.claimFormTemplates.initDefaultTemplates);
  const removeTemplate = useMutation(api.insurance.claimFormTemplates.remove);

  const isLoading = orgLoading || templates === undefined;
  const isEmpty = !isLoading && (templates ?? []).length === 0;

  async function handleInitTemplates() {
    if (!currentOrg) return;
    setIsIniting(true);
    try {
      await initTemplates({ orgId: currentOrg._id });
    } finally {
      setIsIniting(false);
    }
  }

  async function handleDelete(templateId: string) {
    await removeTemplate({
      templateId: templateId as Parameters<typeof removeTemplate>[0]['templateId'], // AS_JUSTIFIED: templateId string from template._id narrowed to typed Id for mutation arg
    });
  }

  if (!orgLoading && !currentOrg) {
    return (
      <div className="min-h-full bg-background-secondary py-8 px-10">
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No organisation found</h3>
          <p className="text-sm text-muted-foreground">
            You need to be part of an organisation to manage claim forms.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background-secondary py-8 px-10">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Claim Form Templates</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isLoading ? 'Loading...' : `${(templates ?? []).length} template${(templates ?? []).length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <div className="flex gap-2">
          {isEmpty && (
            <Button variant="outline" onClick={handleInitTemplates} disabled={isIniting}>
              <Layers className="w-4 h-4 mr-2" />
              {isIniting ? 'Initializing...' : 'Initialize Default Templates'}
            </Button>
          )}
          <Button onClick={() => navigate('/insurance/claim-forms/new')}>
            <Plus className="w-4 h-4 mr-2" />
            Create Custom Template
          </Button>
        </div>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => <TemplateCardSkeleton key={i} />)}
        </div>
      )}

      {isEmpty && (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <FileText className="w-12 h-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-1">No claim form templates yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Initialize with predefined templates for health, motor, and home insurance.
          </p>
          <Button onClick={handleInitTemplates} disabled={isIniting}>
            <Layers className="w-4 h-4 mr-2" />
            {isIniting ? 'Initializing...' : 'Initialize Default Templates'}
          </Button>
        </div>
      )}

      {!isLoading && !isEmpty && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {(templates ?? []).map(t => (
            <TemplateCard
              key={t._id}
              template={t as IClaimFormTemplate} // AS_JUSTIFIED: Convex return narrowed to typed IClaimFormTemplate matching return validator
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
